import React from 'react';
import { Users, Code, Star, Building, Clock, Award, MapPin, BookOpen } from 'lucide-react';
import { cppMockData } from './cpp-mock';

const featureIcons = {
  'building-blocks': Building,
  'code': Code,
  'users': Users,
  'star': Star
};

const CppFeatures = () => {
  return (
    <section id="features" className="cpp-section">
      <div className="cpp-container">
        <div className="text-center mb-12">
          <h2 className="cpp-heading-1 mb-4">Why Choose Our C++ Course?</h2>
          <p className="cpp-body-large max-w-3xl mx-auto">
            Learn C++ programming in the heart of Amman with our proven step-by-step methodology. 
            Perfect for absolute beginners who want to build a strong foundation.
          </p>
        </div>
        
        {/* Main Features Grid */}
        <div className="cpp-grid mb-16">
          {cppMockData.features.map((feature, index) => {
            const IconComponent = featureIcons[feature.icon] || Code;
            
            return (
              <div key={index} className="cpp-card text-center group hover:scale-105 transition-all duration-300">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <IconComponent className="w-10 h-10 text-white" />
                </div>
                
                <h3 className="cpp-heading-3 text-white mb-4">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            );
          })}
        </div>
        
        {/* Course Information Cards */}
        <div className="cpp-grid-2 mb-16">
          {/* Location & Schedule */}
          <div className="cpp-card">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="cpp-heading-3 text-white">Location & Schedule</h3>
                <p className="text-green-400 text-sm">In-Person Classes in Amman</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {cppMockData.schedule.map((batch, index) => (
                <div key={index} className="p-4 bg-gray-800/50 rounded-lg border-l-4 border-green-400">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-semibold">{batch.batch}</h4>
                    <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full">
                      {batch.status}
                    </span>
                  </div>
                  <div className="text-gray-400 text-sm space-y-1">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{batch.days} • {batch.time}</span>
                    </div>
                    <div className="text-blue-400">Starts: {batch.startDate}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Course Details */}
          <div className="cpp-card">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="cpp-heading-3 text-white">Course Details</h3>
                <p className="text-purple-400 text-sm">Complete Program Information</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span className="text-gray-400">Course Fee</span>
                <span className="text-white font-semibold text-lg">{cppMockData.courseInfo.price}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span className="text-gray-400">Duration</span>
                <span className="text-blue-400 font-semibold">{cppMockData.courseInfo.duration}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span className="text-gray-400">Class Size</span>
                <span className="text-green-400 font-semibold">{cppMockData.courseInfo.classSize}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span className="text-gray-400">A laptop</span>
                <span className="text-purple-400 font-semibold">Required for the course</span>
              </div>
            </div>
            
            <div className="mt-6">
              <button 
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  contactSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="cpp-btn-primary w-full"
              >
                <Award className="w-5 h-5 mr-2" />
                Reserve Your Spot Now
              </button>
            </div>
          </div>
        </div>
        
        {/* Problem Solving Showcase */}
        <div className="text-center">
          <div className="cpp-card max-w-4xl mx-auto bg-gradient-to-r from-green-900/20 to-blue-900/20 border-green-500/30">
            <h3 className="cpp-heading-2 text-white mb-6">💡 Problem-Solving Approach</h3>
            
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <h4 className="text-green-400 font-semibold mb-2">Understand</h4>
                <p className="text-gray-400 text-sm">Break down the problem into smaller, manageable pieces</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <h4 className="text-blue-400 font-semibold mb-2">Plan</h4>
                <p className="text-gray-400 text-sm">Design your solution step-by-step using C++ concepts</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <h4 className="text-purple-400 font-semibold mb-2">Code</h4>
                <p className="text-gray-400 text-sm">Implement your solution and test it thoroughly</p>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-gray-300 mb-4">
                <strong className="text-green-400">50+</strong> hands-on problems from basic to advanced level
              </p>
              <div className="text-sm text-gray-400">
                "Programming is learned by writing programs" - our philosophy in action
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CppFeatures;